import React, { useEffect, useState, useRef } from "react";
import { Sparkles, Check, Plus, Loader2 } from "lucide-react";
import api from "../api";
import { formatEUR, UNIT_LABELS } from "../utils/format";

/**
 * AI-powered duplicate-ingredient suggestion popover.
 *
 * Props:
 *   name        : current input value (string)
 *   excludeId   : ingredient id to ignore (when editing)
 *   onAccept    : (ingredient) => void  -- user picked an existing match
 *   onDismiss   : () => void           -- user wants to create new
 *   debounceMs  : default 300
 *   useAi       : default true (calls F&B Intelligence for top-3 semantic confirmation)
 *
 * Renders nothing when name length < 3 or no matches found.
 */
export default function IngredientMatchSuggestion({ name, excludeId, onAccept, onDismiss, onTopMatch, debounceMs = 300, useAi = true }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastQueryRef = useRef("");

  useEffect(() => {
    setDismissed(false);
  }, [name]);

  useEffect(() => {
    if (!name || name.trim().length < 3) {
      setMatches([]);
      onTopMatch?.(null);
      return;
    }
    const q = name.trim();
    if (q === lastQueryRef.current) return;
    lastQueryRef.current = q;
    const tm = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/ingredients/match", { name: q, use_ai: useAi });
        const filtered = (data.matches || []).filter((m) => !excludeId || m.id !== excludeId);
        setMatches(filtered);
        onTopMatch?.(filtered[0] || null);
      } catch {
        setMatches([]);
        onTopMatch?.(null);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(tm);
  }, [name, excludeId, debounceMs, useAi, onTopMatch]);

  if (dismissed || matches.length === 0) return null;

  const best = matches[0];
  const aiConfirmed = best.ai_confirmed === true;

  return (
    <div
      className="rounded-lg border border-brand/30 bg-brand/[0.08] px-3 py-2.5 mt-2 space-y-2"
      data-testid="ingredient-match-suggestion"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-brand font-medium">
        <Sparkles size={11} />
        {loading
          ? "Suche nach Duplikaten…"
          : aiConfirmed
            ? "AI-bestätigtes Duplikat gefunden"
            : `Möglicherweise schon vorhanden (${matches.length})`}
      </div>
      {!loading && (
        <ul className="space-y-1.5">
          {matches.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-2 bg-bg-void/40 rounded px-2 py-1.5"
              data-testid={`match-row-${m.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-ink-primary truncate">{m.name}</span>
                  <span className="text-[10px] text-ink-muted">
                    · {m.category} · {UNIT_LABELS[m.unit] || m.unit} · {formatEUR(m.price_per_unit, 4)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-ink-muted">
                  <span>Ähnlichkeit {m.score}%</span>
                  {m.ai_confirmed === true && <span className="text-ok">AI: gleich</span>}
                  {m.ai_confirmed === false && <span className="text-warn">AI: unterschiedlich</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onAccept?.(m)}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-brand/15 text-brand hover:bg-brand/25 transition-colors"
                data-testid={`match-accept-${m.id}`}
              >
                <Check size={11} /> Übernehmen
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <Loader2 size={12} className="animate-spin" /> AI prüft Ähnlichkeit…
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => { setDismissed(true); onDismiss?.(); }}
          className="text-[11px] text-ink-muted hover:text-ink-primary inline-flex items-center gap-1"
          data-testid="match-create-new"
        >
          <Plus size={11} /> Trotzdem neu anlegen
        </button>
      </div>
    </div>
  );
}
